import React from 'react';
import styles from './ResumeUpload.module.css';
import * as utils from '../../utils/utils';

export default class ResumeUpload extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			file: null,
			isUploading: false,
			isDragActive: false,
			errorText: '',
			infoText: 'Upload your resume as a PDF to continue to your dashboard.',
		};
	}

	getValidatedPdfFile = (file) => {
		if (!file) {
			return null;
		}

		const isPdfMime = file.type === 'application/pdf';
		const isPdfName = file.name.toLowerCase().endsWith('.pdf');

		if (!isPdfMime && !isPdfName) {
			return null;
		}

		return file;
	};

	onFileChange = (event) => {
		const file = event.target.files && event.target.files[0];

		if (!file) {
			this.setState({
				file: null,
				errorText: '',
			});
			return;
		}

		const validatedFile = this.getValidatedPdfFile(file);

		if (!validatedFile) {
			this.setState({
				file: null,
				errorText: 'Please select a PDF file.',
			});
			return;
		}

		this.setState({
			file: validatedFile,
			errorText: '',
		});
	};

	onDragOver = (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (!this.state.isDragActive) {
			this.setState({ isDragActive: true });
		}
	};

	onDragLeave = (event) => {
		event.preventDefault();
		event.stopPropagation();
		this.setState({ isDragActive: false });
	};

	onDrop = (event) => {
		event.preventDefault();
		event.stopPropagation();

		const file = event.dataTransfer?.files && event.dataTransfer.files[0];
		const validatedFile = this.getValidatedPdfFile(file);

		if (!validatedFile) {
			this.setState({
				isDragActive: false,
				file: null,
				errorText: 'Please drop a PDF file.',
			});
			return;
		}

		this.setState({
			isDragActive: false,
			file: validatedFile,
			errorText: '',
		});
	};

	uploadResume = async () => {
		const {
			file,
		} = this.state;

		if (!file) {
			this.setState({ errorText: 'Please choose a PDF resume first.' });
			return;
		}

		this.setState({
			isUploading: true,
			errorText: '',
			infoText: 'Preparing secure upload...',
		});

		try {
			const uploadData = await utils.sendRequest('/resume/getResumeUploadData', {});
			const {
				originalMediaSignedUrl,
			} = uploadData || {};

			if (!originalMediaSignedUrl) {
				throw new Error('Missing signed upload URL.');
			}

			this.setState({ infoText: 'Uploading your resume...' });

			const uploadResponse = await fetch(originalMediaSignedUrl, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/pdf',
					'X-Amz-ACL': 'private',
				},
				body: file,
			});

			if (!uploadResponse.ok) {
				throw new Error(`Upload failed (${uploadResponse.status}).`);
			}

			window.location.href = '/dashboard';
		}
		catch (error) {
			console.log('resume upload error', error);
			this.setState({
				isUploading: false,
				errorText: 'Could not upload resume. Please try again.',
				infoText: 'Upload your resume as a PDF to continue to your dashboard.',
			});
		}
	};

	render() {
		const {
			file,
			isUploading,
			isDragActive,
			errorText,
			infoText,
		} = this.state;

		return (
			<div className={styles.pageContainer}>
				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

				<div className={styles.layoutShell}>
					<section className={styles.leftPanel}>
						<div className={styles.brandBadge}>HQVibe</div>
						<h1 className={styles.title}>One last step before your dashboard.</h1>
						<p className={styles.subtitle}>
							Add your resume so we can personalize your experience and recommend higher-signal opportunities.
						</p>
					</section>

					<section className={styles.cardPanel}>
						<h2 className={styles.formTitle}>Upload Resume</h2>
						<p className={styles.formText}>{infoText}</p>

						<div className={styles.form}>
							<div className={styles.fieldGroup}>
								<label className={styles.label} htmlFor='resume-file'>Resume PDF</label>
								<div
									className={`${styles.dropZone} ${isDragActive ? styles.dropZoneActive : ''} ${isUploading ? styles.dropZoneDisabled : ''}`}
									onDragOver={this.onDragOver}
									onDragLeave={this.onDragLeave}
									onDrop={this.onDrop}
								>
									<input
										id='resume-file'
										type='file'
										accept='application/pdf,.pdf'
										className={styles.hiddenFileInput}
										onChange={this.onFileChange}
										disabled={isUploading}
									/>
									<label className={styles.dropZoneInner} htmlFor='resume-file'>
										<span className={styles.dropZoneTitle}>Drag and drop your resume here</span>
										<span className={styles.dropZoneText}>or click to choose a PDF file</span>
									</label>
								</div>
								{
									file &&
									<p className={styles.fileName}>{file.name}</p>
								}
							</div>

							<button
								className={styles.primaryButton}
								type='button'
								onClick={this.uploadResume}
								disabled={isUploading}
							>
								{isUploading ? 'Uploading...' : 'Upload & Continue'}
							</button>
						</div>

						{
							errorText !== '' &&
							<div className={styles.errorBox}>{errorText}</div>
						}
					</section>
				</div>
			</div>
		);
	}
}
